import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch } from "~/store/hooks";
import { injectIntl, useIntl } from "@ai360/core/intl";
import { preventBubbleUp, toCamelCase } from "~/admin/utils";
import { setBreadcrumbs } from "~/sliding-panel/data/actions";
import { DataTable, LoaderTypes, Loader, DialogBox, DialogBoxFooterType } from "~/core";

import { ISearchData, ISortOptions } from "~/core/components/tables/interfaces";
import ApiResultsWrapper from "~/core/api/components/api-results-wrapper";
import HierarchyFilter from "../component/hierarchy-filter";
import { DefinedLegendAPI } from "@ai360/core";

import DefinedLegendAddEditPanel from "./add-edit/add-edit-panel";
import { model, service } from "./data";
import { messages } from "../defined-legend/i18n-messages";

type IDefinedLegendGridRow = DefinedLegendAPI.IDefinedLegendGridRow;
type IPageOptions = DefinedLegendAPI.IDefinedLegendPageOptions;

type DefinedLegendFilters = DefinedLegendAPI.DefinedLegendFilters;

type DefinedLegendGridRow = IDefinedLegendGridRow & {
    location: string;
};

const EMPTY_DEFINED_LEGEND = { results: [] as IDefinedLegendGridRow[] };

export const DefinedLegend_ = () => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();

    const [filter, setFilter] = useState<DefinedLegendFilters>({});
    const [showAddEditPanel, setShowAddEditPanel] = useState(false);
    const [autoSearchData, setAutoSearchData] = useState<ISearchData>();
    const [itemToDelete, setItemToDelete] = useState<IDefinedLegendGridRow | null>(null);
    const [selectedRow, setSelectedRow] = useState<IDefinedLegendGridRow | null>(null);
    const [sortOptions, setSortOptions] = useState<ISortOptions>({});
    const [pageOptions, setPageOptions] = useState<IPageOptions>({ pageSize: 50, skip: 0 });

    const sortOptionsRef = useRef<ISortOptions>(sortOptions);

    const [
        getDefinedLegends,
        { data: definedLegendData = EMPTY_DEFINED_LEGEND, isFetching: isGetLoading },
    ] = DefinedLegendAPI.useLazyGetDefinedLegendsQuery();

    useEffect(() => {
        sortOptionsRef.current = sortOptions;
    }, [sortOptions]);

    useEffect(() => {
        getDefinedLegends(
            {
                definedLegendFilter: filter,
                definedLegendSort: sortOptionsRef.current,
                definedLegendPageOptions: {
                    pageSize: pageOptions?.pageSize,
                    skip: pageOptions?.skip,
                },
            } as any,
            true
        );
    }, [getDefinedLegends, pageOptions?.pageSize, pageOptions?.skip, filter]);

    const orgLevels = useMemo(() => {
        return new Set(
            definedLegendData.results
                ?.map((record: IDefinedLegendGridRow) => record.locations.map((l) => l.name))
                .flat()
        );
    }, [definedLegendData]);

    const [deleteDefinedLegend, { isLoading: isDeleteLoading }] =
        DefinedLegendAPI.useDeleteDefinedLegendMutation();

    useEffect(() => {
        dispatch(setBreadcrumbs());
    }, [showAddEditPanel]);

    const gridData = useMemo(() => {
        let filteredData = definedLegendData.results?.map(
            (record) =>
                ({
                    ...record,
                    location: record.locations
                        .map((l) => l.name)
                        .sort()
                        .join(", "),
                    layerTypeName: record.layerTypeName,
                    attributeName: record.attributeName,
                    cropName: record.cropName,
                    cropPurposeName: record.cropPurposeName,
                    productName: record.productName,
                    canDelete: true,
                } as unknown as DefinedLegendGridRow)
        );

        if (filter.layerTypeName) {
            filteredData = filteredData.filter((record) =>
                record.layerTypeName.includes(filter.layerTypeName)
            );
        }
        if (filter.location) {
            filteredData = filteredData.filter((record) =>
                record.location.includes(filter.location)
            );
        }
        if (filter.attributeName) {
            filteredData = filteredData.filter((record) =>
                record.attributeName.includes(filter.attributeName)
            );
        }
        if (filter.cropName) {
            filteredData = filteredData.filter((record) =>
                record.cropName?.includes(filter.cropName)
            );
        }
        if (filter.cropPurposeName) {
            filteredData = filteredData.filter((record) =>
                record.cropPurposeName?.includes(filter.cropPurposeName)
            );
        }
        if (filter.productName) {
            filteredData = filteredData.filter((record) =>
                record.productName?.includes(filter.productName)
            );
        }
        const sortAttribute = toCamelCase(sortOptions?.FieldName || model.PROPS_LAYER_TYPE_NAME);
        const sortAsc = (sortOptions?.Sort?.Direction || "ASC") === "ASC";
        filteredData = filteredData?.sort((a, b) => {
            const aAttr = a[sortAttribute];
            const bAttr = b[sortAttribute];
            if (aAttr < bAttr) {
                return sortAsc ? -1 : 1;
            }
            if (aAttr > bAttr) {
                return sortAsc ? 1 : -1;
            }
            return 0;
        });

        return filteredData ?? [];
    }, [definedLegendData, filter, sortOptions]);

    const onShowAddEditPanel = (
        _,
        action: "ADD" | "EDIT",
        editData: { ownerDefinedLegendGuid: string }
    ) => {
        let record: IDefinedLegendGridRow | null = null;
        if (action === "EDIT") {
            record = definedLegendData.results.find(
                (record) => record.ownerDefinedLegendGuid === editData.ownerDefinedLegendGuid
            );
        }
        setSelectedRow(record);
        setShowAddEditPanel(true);
    };

    const getAutoSearchList = (attr: string, value: string) => {
        const attrName = toCamelCase(attr);
        const autoSearchData: ISearchData = {
            searchName: attrName,
            response: [],
        };
        if (attrName === model.PROPS_LOCATION) {
            autoSearchData.response = Array.from(orgLevels)
                .filter((level) => level.toLowerCase().includes(value.toLowerCase()))
                .map((level) => ({ value: level }));
        } else if (attrName === model.PROPS_LAYER_TYPE_NAME) {
            autoSearchData.response = Array.from(
                new Set(gridData?.map((record) => record[model.PROPS_LAYER_TYPE_NAME]))
            )
                .filter((layerTypeName) =>
                    layerTypeName.toString().toLowerCase().includes(value.toLowerCase())
                )
                .map((layerTypeName) => ({ value: layerTypeName }));
        } else if (attrName === model.PROPS_ATTRIBUTE_NAME) {
            autoSearchData.response = Array.from(
                new Set(gridData?.map((record) => record[model.PROPS_ATTRIBUTE_NAME]))
            )
                .filter((attributeName) =>
                    attributeName.toString().toLowerCase().includes(value.toLowerCase())
                )
                .map((attributeName) => ({ value: attributeName }));
        } else if (attrName === model.PROPS_CROP_NAME) {
            autoSearchData.response = Array.from(
                new Set(gridData?.map((record) => record[model.PROPS_CROP_NAME]))
            )
                .filter(Boolean)
                .filter((cropName) =>
                    cropName.toString().toLowerCase().includes(value.toLowerCase())
                )
                .map((cropName) => ({ value: cropName }));
        } else if (attrName === model.PROPS_DL_CROP_PURPOSE_NAME) {
            autoSearchData.response = Array.from(
                new Set(gridData?.map((record) => record[model.PROPS_DL_CROP_PURPOSE_NAME]))
            )
                .filter(Boolean)
                .filter((cropPurposeName) =>
                    cropPurposeName.toString().toLowerCase().includes(value.toLowerCase())
                )
                .map((cropPurposeName) => ({ value: cropPurposeName }));
        } else if (attrName === model.PROPS_DL_PRODUCT_NAME) {
            autoSearchData.response = Array.from(
                new Set(gridData?.map((record) => record[model.PROPS_DL_PRODUCT_NAME]))
            )
                .filter(Boolean)
                .filter((productName) =>
                    productName.toString().toLowerCase().includes(value.toLowerCase())
                )
                .map((productName) => ({ value: productName }));
        }
        setAutoSearchData(autoSearchData);
        return autoSearchData;
    };

    const onFilterChange = (attr: string, value: string, _: any) => {
        const attrName = toCamelCase(attr);
        const filterQuery = { ...filter, [attrName]: value ? value : undefined };
        setFilter(filterQuery);
    };

    const onDelete = () => {
        if (itemToDelete === null) {
            return;
        }
        const deletePromise = deleteDefinedLegend({
            ownerDefinedLegendGuid: itemToDelete[model.PROPS_DEFINED_LEGEND_GUID],
        })
            .unwrap()
            .catch(() => {
                // Handled by ApiResultsWrapper
            });

        Promise.all([deletePromise]).then(() => {
            setItemToDelete(null);
        });
    };

    const onDeleteAction = (deleteData) => {
        if (deleteData.selectedItems?.length === 0) return;
        if (Array.isArray(deleteData.selectedItems) && deleteData.selectedItems.length == 1) {
            setItemToDelete(deleteData.selectedItems[0] as IDefinedLegendGridRow);
        }
    };

    const isLoading = isGetLoading || isDeleteLoading;

    return (
        <ApiResultsWrapper clearErrorOnUnmount={false}>
            <div className="content-table-container defined-legend-table">
                {!isLoading ? null : <Loader type={LoaderTypes.LINE_SCALE} />}
                <div className="filter-header-container">
                    <div className="filter-container">
                        <HierarchyFilter
                            onFilterChange={(location) =>
                                onFilterChange(
                                    model.PROPS_LOCATION,
                                    location ? location[model.PROPS_ORG_LEVEL_NAME] : null,
                                    null
                                )
                            }
                        />
                    </div>
                </div>
                <DataTable
                    formatMessage={formatMessage as any}
                    autoSearchList={autoSearchData}
                    isEditable
                    isCheckbox={false}
                    messages={messages as any}
                    getAutoSearchList={getAutoSearchList}
                    onFilterChange={onFilterChange}
                    onSortOption={({ sortOptions: nextSortOptions }) => {
                        setSortOptions(nextSortOptions || service.defaultSort);
                    }}
                    onPaginatorChange={(index) => {
                        if (index.pageSize) {
                            setPageOptions((prevPageOptions) => ({
                                ...prevPageOptions,
                                pageSize: Number(index.pageSize),
                                skip: index.skip || 0,
                            }));
                        }
                    }}
                    openAddEditPanel={onShowAddEditPanel}
                    records={gridData}
                    service={service}
                    totalCount={gridData.length}
                    deleteSelected={onDeleteAction}
                    setLastEditedRow={() => {
                        /* purposely left blank */
                    }}
                />
                {!showAddEditPanel ? null : (
                    <form onSubmit={(event) => preventBubbleUp(event)}>
                        <DefinedLegendAddEditPanel
                            closePanel={() => {
                                setSelectedRow(null);
                                setShowAddEditPanel(false);
                            }}
                            selectedRow={selectedRow as IDefinedLegendGridRow}
                        />
                    </form>
                )}
            </div>
            <DialogBox
                action={"Delete"}
                draggable={true}
                className="delete-confirmation-modal"
                footerType={DialogBoxFooterType.ACTION_CANCEL}
                isOpen={itemToDelete !== null}
                onAction={onDelete}
                onClose={() => {
                    setItemToDelete(null);
                }}
                title={formatMessage(messages.deleteConfirmationTitle)}
                testId="defined-legend-confirm-delete-modal"
            >
                <p>{formatMessage(messages.deleteConfirmationMessage)}</p>
            </DialogBox>
        </ApiResultsWrapper>
    );
};

export const DefinedLegend = injectIntl(DefinedLegend_);
